import React, {useState, useEffect, useRef} from 'react';
import {
	Box, Grid, Container, Typography, Card, CardContent, CardHeader, Button, IconButton,
	Avatar, Chip, TextField, InputAdornment, Select, MenuItem, Dialog, DialogTitle,
	DialogContent, DialogContentText, DialogActions, Tabs, Tab, CircularProgress, Drawer,
	Table, TableHead, TableRow, TableCell, TableBody, TableContainer
} from '@mui/material';
import {styled, useTheme} from '@mui/material/styles';
import {useMediaQuery} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import {useNavigate} from 'react-router-dom';
import ClearIcon from '@mui/icons-material/Clear';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import {LoadingButton} from '@mui/lab';
import {ThemeProvider} from "@mui/material/styles";
import {buttonTheme} from "../../theme";
import { config } from "../../config";
import { getVendorId, getUserName } from "../../utils/authSession";
import { aiErrorMessage, AI_IMAGE_TOO_LARGE } from "../../utils/aiErrors";

const cardSurface = {
	border: '1px solid #E5E7EB',
	boxShadow: '0px 0px 0px #e5e5e5',
	borderRadius: '8px',
	backgroundColor: '#FFF',
};

const API_BASE = `${config.API_MSWSSTOREMANAGER_BASE_URL}/v2`;

export default function ManageProducts() {
	const navigate = useNavigate();
	const theme = useTheme();
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
	const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

	const [vendorId, setVendorId] = useState(getVendorId());
	const sessionToken = () =>
		sessionStorage.getItem("sessionToken") || localStorage.getItem("sessionToken");

	const [departments, setDepartments] = useState([]);
	const [categories, setCategories] = useState([]);
	const [subCategories, setSubCategories] = useState([]);

	const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
	const [selectedCategoryId, setSelectedCategoryId] = useState("");
	const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("");

	const [addProductDepartment, setAddProductDepartment] = useState("");
	const [addProductCategory, setAddProductCategory] = useState("");
	const [addProductSubCategory, setAddProductSubCategory] = useState("");
	const [dialogCategories, setDialogCategories] = useState([]);
	const [dialogSubCategories, setDialogSubCategories] = useState([]);

	const [products, setProducts] = useState([]);
	const [loadingProducts, setLoadingProducts] = useState(false);

	const [stats, setStats] = useState({total: 0, outOfStock: 0, lowStock: 0, totalValue: 0});
	const [allProducts, setAllProducts] = useState([]);

	const [searchType, setSearchType] = useState("name");
	const [searchQuery, setSearchQuery] = useState("");
	const [searchValueMin, setSearchValueMin] = useState("");
	const [searchValueMax, setSearchValueMax] = useState("");

	const [isAddProductDialogOpen, setIsAddProductDialogOpen] = useState(false);
	const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
	const [categoriesDrawerOpen, setCategoriesDrawerOpen] = useState(false);

	const [aiTab, setAiTab] = useState("image");
	const [imageFile, setImageFile] = useState(null);
	const [imagePreview, setImagePreview] = useState("");
	const [aiDescription, setAiDescription] = useState("");
	const [aiApiError, setAiApiError] = useState(false);
	const [aiErrorMsg, setAiErrorMsg] = useState("");
	const fileInputRef = useRef(null);

	const [loadBackdrop, setLoadBackdrop] = useState(false);

	const [deleteTarget, setDeleteTarget] = useState(null);
	const [statusToast, setStatusToast] = useState({open: false, severity: "success", message: ""});
	const [searchLoading, setSearchLoading] = useState(false);

	const ADD_NEW_DEPT = "__add_new_dept__";
	const ADD_NEW_CAT = "__add_new_cat__";
	const ADD_NEW_SUB = "__add_new_sub__";

	const goToAddDepartment = () => {
		localStorage.setItem('visitedMp', 'true');
		navigate('/md', {state: {fromManageProducts: true, vendorId}});
	};

	const SEL_KEY = "mp.selection";

	const saveSelection = (sel) => {
		sessionStorage.setItem(SEL_KEY, JSON.stringify(sel));
	};
	const loadSelection = () => {
		try {
			return JSON.parse(sessionStorage.getItem(SEL_KEY) || "{}");
		} catch {
			return {};
		}
	};
	const clearSelection = () => sessionStorage.removeItem(SEL_KEY);

	const goToAddCategory = () => {
		if (!selectedDepartmentId) {
			alert('Please select a department first.');
			return;
		}
		const dept = departments.find(d => d.id === selectedDepartmentId);
		localStorage.setItem('visitedMd', 'true');
		navigate('/sc', {
			state: {
				fromManageProducts: true,
				id: selectedDepartmentId,
				vendorId,
				departmentName: dept?.departmentName || "",
			},
		});
	};

	const goToAddSubCategory = () => {
		if (!selectedDepartmentId || !selectedCategoryId) {
			alert('Please select a department and category first.');
			return;
		}
		const dept = departments.find(d => d.id === selectedDepartmentId);
		const cat = categories.find(c => c.id === selectedCategoryId);
		localStorage.setItem('visitedSc', 'true');
		navigate('/ssc', {
			state: {
				fromManageProducts: true,
				id: selectedCategoryId,
				vendorId,
				categoryName: cat?.categoryName || "",
				departmentName: dept?.departmentName || "",
				deptId: selectedDepartmentId,
			},
		});
	};

	useEffect(() => {
		localStorage.setItem('visitedMp', 'true');
	}, []);

	useEffect(() => {
		if (!vendorId) return;
		setLoadBackdrop(true);

		const saved = loadSelection(); // { departmentId, categoryId, subCategoryId }

		fetch(`${API_BASE}/getDepartmentsByVendor`, {
			method: 'POST',
			headers: {Accept: "*/*", "Content-Type": "application/json", "X-Session-Token": sessionToken()},
			body: JSON.stringify({vendorId})
		})
			.then(res => res.json())
			.then(async (deptArrRaw) => {
				const deptArr = Array.isArray(deptArrRaw) ? deptArrRaw : [];
				setDepartments(deptArr);
				if (!deptArr.length) {
					setLoadBackdrop(false);
					return;
				}

				const deptId = (saved.departmentId && deptArr.some(d => d.id === saved.departmentId))
					? saved.departmentId : deptArr[0].id;
				setSelectedDepartmentId(deptId);

				const catRes = await fetch(`${API_BASE}/getCategoriesByVendor`, {
					method: 'POST',
					headers: {"Content-Type": "application/json", "X-Session-Token": sessionToken()},
					body: JSON.stringify({vendorId, parentCatagoryId: deptId})
				});
				const catArrRaw = await catRes.json();
				const catArr = Array.isArray(catArrRaw) ? catArrRaw : [];

				setCategories(catArr);
				if (!catArr.length) {
					setLoadBackdrop(false);
					return;
				}

				const catId = (saved.categoryId && catArr.some(c => c.id === saved.categoryId))
					? saved.categoryId : catArr[0].id;
				setSelectedCategoryId(catId);

				const subRes = await fetch(`${API_BASE}/getSubCategoriesByVendor`, {
					method: 'POST',
					headers: {"Content-Type": "application/json", "X-Session-Token": sessionToken()},
					body: JSON.stringify({vendorId, parentCatagoryId: catId})
				});
				const subArrRaw = await subRes.json();
				const subArr = Array.isArray(subArrRaw) ? subArrRaw : [];
				setSubCategories(subArr);
				if (!subArr.length) {
					setLoadBackdrop(false);
					return;
				}

				const subId = (saved.subCategoryId && subArr.some(s => s.id === saved.subCategoryId))
					? saved.subCategoryId : subArr[0].id;
				setSelectedSubCategoryId(subId);

				const deptObj = deptArr.find(d => d.id === deptId);
				const catObj = catArr.find(c => c.id === catId);
				const subObj = subArr.find(s => s.id === subId);
				if (deptObj && catObj && subObj) {
					await loadProducts(deptObj.departmentName, catObj.categoryName, subObj.subCategoryName);
				}
				setLoadBackdrop(false);
			})
			.catch(err => {
				console.error("Error fetching departments:", err);
				setLoadBackdrop(false);
			});
	}, [vendorId]);

	const computeStats = (productList) => {
		let total = productList.length;
		let outOfStock = 0;
		let lowStockCount = 0;
		let totalVal = 0;
		productList.forEach(p => {
			const stock = p.inStock || 0;
			const lowStockThresh = p.lowStock || 0;
			const price = p.pricePerUnit || 0;
			if (stock === 0) outOfStock += 1;
			else if (stock > 0 && lowStockThresh > 0 && stock < lowStockThresh) lowStockCount += 1;
			totalVal += price * stock;
		});
		setStats({
			total: total,
			outOfStock: outOfStock,
			lowStock: lowStockCount,
			totalValue: totalVal
		});
	};

	useEffect(() => {
		if (!selectedDepartmentId) return;

		setSelectedCategoryId("");
		setSelectedSubCategoryId("");
		setProducts([]);
		setSubCategories([]);
		computeStats([]);

		setLoadBackdrop(true);
		fetch(`${API_BASE}/getCategoriesByVendor`, {
			method: 'POST',
			headers: {
				"Content-Type": "application/json",
				"X-Session-Token": sessionToken()
			},
			body: JSON.stringify({vendorId, parentCatagoryId: selectedDepartmentId})
		})
			.then(res => res.json())
			.then(data => {
				setCategories(Array.isArray(data) ? data : []);
				setLoadBackdrop(false);
			})
			.catch(error => {
				console.error("Error fetching categories:", error);
				setLoadBackdrop(false);
			});
	}, [selectedDepartmentId]);

	useEffect(() => {
		if (!selectedCategoryId) return;

		setSelectedSubCategoryId("");
		setProducts([]);
		computeStats([]);

		setLoadBackdrop(true);
		fetch(`${API_BASE}/getSubCategoriesByVendor`, {
			method: 'POST',
			headers: {
				"Content-Type": "application/json",
				"X-Session-Token": sessionToken()
			},
			body: JSON.stringify({vendorId, parentCatagoryId: selectedCategoryId})
		})
			.then(res => res.json())
			.then(data => {
				setSubCategories(Array.isArray(data) ? data : []);
				setLoadBackdrop(false);
			})
			.catch(error => {
				console.error("Error fetching subcategories:", error);
				setLoadBackdrop(false);
			});
	}, [selectedCategoryId]);

	useEffect(() => {
		if (!selectedSubCategoryId || !selectedDepartmentId || !selectedCategoryId) return;
		const deptObj = departments.find(d => d.id === selectedDepartmentId);
		const catObj = categories.find(c => c.id === selectedCategoryId);
		const subObj = subCategories.find(s => s.id === selectedSubCategoryId);
		if (deptObj && catObj && subObj) {
			loadProducts(deptObj.departmentName, catObj.categoryName, subObj.subCategoryName);
		}
	}, [selectedSubCategoryId]);

	const handleDepartmentChange = (event) => {
		const val = event.target.value;
		if (val === ADD_NEW_DEPT) {
			event.target.value = selectedDepartmentId || "";
			goToAddDepartment();
			return;
		}
		setSelectedDepartmentId(val);
		saveSelection({departmentId: val, categoryId: "", subCategoryId: ""}); // reset lower levels
	};

	const handleCategoryChange = (event) => {
		const val = event.target.value;
		if (val === ADD_NEW_CAT) {
			event.target.value = selectedCategoryId || "";
			goToAddCategory();
			return;
		}
		setSelectedCategoryId(val);
		saveSelection({...loadSelection(), categoryId: val, subCategoryId: ""});
	};

	const handleSubCategoryChange = (event) => {
		const val = event.target.value;
		if (val === ADD_NEW_SUB) {
			event.target.value = selectedSubCategoryId || "";
			goToAddSubCategory();
			return;
		}
		setSelectedSubCategoryId(val);
		saveSelection({...loadSelection(), subCategoryId: val});
	};

	const handleAddDeptChange = (event) => {
		const deptId = event.target.value;
		setAddProductDepartment(deptId);
		setAddProductCategory("");
		setAddProductSubCategory("");
		setDialogCategories([]);
		setDialogSubCategories([]);
		if (deptId) {

			const deptObj = departments.find(d => d.id === deptId);
			fetch(`${API_BASE}/getCategoriesByVendor`, {
				method: 'POST',
				headers: {
					"Content-Type": "application/json",
					"X-Session-Token": sessionToken()
				},
				body: JSON.stringify({vendorId: vendorId, parentCatagoryId: deptId})
			})
				.then(res => res.json())
				.then(data => {
					setDialogCategories(data);
				})
				.catch(error => console.error("Error fetching categories (dialog):", error));
		}
	};
	const handleAddCategoryChange = (event) => {
		const catId = event.target.value;
		setAddProductCategory(catId);
		setAddProductSubCategory("");
		setDialogSubCategories([]);
		if (catId) {
			fetch(`${API_BASE}/getSubCategoriesByVendor`, {
				method: 'POST',
				headers: {
					"Content-Type": "application/json",
					"X-Session-Token": sessionToken()
				},
				body: JSON.stringify({vendorId: vendorId, parentCatagoryId: catId})
			})
				.then(res => res.json())
				.then(data => {
					setDialogSubCategories(data);
				})
				.catch(error => console.error("Error fetching subcategories (dialog):", error));
		}
	};
	const handleAddSubCategoryChange = (event) => {
		setAddProductSubCategory(event.target.value);
	};

	const handleAddProductFromSelected = () => {
		const deptObj = departments.find(d => d.id === selectedDepartmentId);
		const catObj = categories.find(c => c.id === selectedCategoryId);
		const subObj = subCategories.find(s => s.id === selectedSubCategoryId);

		// Safety guard – should never happen because button is disabled otherwise
		if (!deptObj || !catObj || !subObj) return;

		saveSelection({
			departmentId: selectedDepartmentId,
			categoryId: selectedCategoryId,
			subCategoryId: selectedSubCategoryId
		});

		setLoadBackdrop(true);

		fetch(`${API_BASE}/addNewProduct`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Session-Token': sessionToken()
			},
			body: JSON.stringify({
				subCategory: subObj.subCategoryName,
				venderLoggedUserName: getUserName(),
				vendorId: vendorId,
				department: deptObj.departmentName,
				category: catObj.categoryName
			})
		})
			.then(res => res.json())
			.then(data => {
				setLoadBackdrop(false);
				navigate(`/ap/${subObj.subCategoryName}`, {
					state: {
						subCategory: subObj.subCategoryName,
						productID: `${data.product.id}`,
						pricingID: `${data.pricing.id}`,
						inventoryID: `${data.inventory.id}`,
						additionalInfoID: `${data.additionInfo.id}`,
						fulfillmentID: `${data.fulfilment.id}`,
						imagesID: `${data.images.id}`,
						venderLoggerUserName: getUserName(),
						vendorId: vendorId,
						department: deptObj.departmentName,
						category: catObj.categoryName
					}
				});
			})
			.catch(err => {
				console.error('Error adding new product:', err);
				setLoadBackdrop(false);
			});
	};

	const loadProducts = (departmentName, categoryName, subCategoryName) => {
		setLoadingProducts(true);
		fetch(`${API_BASE}/findProductsByDepartments`, {
			method: 'POST',
			headers: {
				"Content-Type": "application/json",
				"X-Session-Token": sessionToken()
			},
			body: JSON.stringify({
				departmentName,
				categoryName,
				subCategoryName,
				vendorId
			})
		})
			.then(res => res.json())
			.then(data => {
				const list = Array.isArray(data) ? data : [];
				setProducts(list);
				setAllProducts(list);
				computeStats(list);
				setLoadingProducts(false);
			})
			.catch(error => {
				console.error("Error fetching products:", error);
				setLoadingProducts(false);
			});
	};

	const getFilteredProducts = () => {
		if (!searchQuery && searchType !== "priceRange") {
			return products;
		}
		return products.filter(product => {
			const name = product.name ? product.name.toLowerCase() : "";
			const sku = product.sku ? product.sku.toLowerCase() : "";
			const barcode = product.barcode ? String(product.barcode).toLowerCase() : "";
			const pid = String(product.productId || "");
			const price = product.pricePerUnit || 0;
			if (searchType === "name") {
				return name.includes(searchQuery.toLowerCase());
			}
			if (searchType === "SKU") {
				return sku.includes(searchQuery.toLowerCase());
			}
			if (searchType === "productId") {
				return pid === searchQuery.trim();
			}
			if (searchType === "barcode") {
				return barcode.includes(searchQuery.toLowerCase());
			}
			if (searchType === "priceRange") {
				let min = parseFloat(searchValueMin) || 0;
				let max = parseFloat(searchValueMax) || Number.MAX_VALUE;
				return price >= min && price <= max;
			}
			return true;
		});
	};

	const filteredProducts = getFilteredProducts();

	const handleSearch = () => {
		if (searchType === 'priceRange') {
			const min = parseFloat(searchValueMin);
			const max = parseFloat(searchValueMax);
			if (Number.isNaN(min) || Number.isNaN(max)) {
				alert('Enter valid Min and Max price.');
				return;
			}
		} else if (!searchQuery && searchType !== 'priceRange') {
			const deptObj = departments.find(d => d.id === selectedDepartmentId);
			const catObj = categories.find(c => c.id === selectedCategoryId);
			const subObj = subCategories.find(s => s.id === selectedSubCategoryId);
			if (deptObj && catObj && subObj) {
				loadProducts(deptObj.departmentName, catObj.categoryName, subObj.subCategoryName);
			}
			return;
		}

		setSearchLoading(true);

		let url = `${API_BASE}/productSearchByKVP`;
		let body = {vendorId, key: '', value: ''};

		if (searchType === 'name') {
			url = `${API_BASE}/productSearchByText`;
			body = {vendorId, searchText: searchQuery};
		} else if (searchType === 'priceRange') {
			body = {
				vendorId,
				key: 'priceRange',
				value: `${searchValueMin}-${searchValueMax}`
			};
		} else if (['SKU', 'productId', 'barcode'].includes(searchType)) {
			body = {vendorId, key: searchType, value: searchQuery};
		}

		fetch(url, {
			method: 'POST',
			headers: {'Content-Type': 'application/json', "X-Session-Token": sessionToken()},
			body: JSON.stringify(body)
		})
			.then(res => res.json())
			.then(data => {
				const list = Array.isArray(data) ? data : [];
				setProducts(list);
				setAllProducts(list);
				computeStats(list);
				setSearchLoading(false);
			})
			.catch(err => {
				console.error('Search error:', err);
				setSearchLoading(false);
			});
	};

	const handleSearchKeyDown = (e) => {
		if (e.key === 'Enter') handleSearch();
	};

	const handleSearchTypeChange = (event) => {
		const newType = event.target.value;
		setSearchType(newType);
		setSearchQuery("");
		setSearchValueMin("");
		setSearchValueMax("");
	};

	const handleAddProductContinue = () => {
		const deptObj = departments.find(d => d.id === addProductDepartment);
		const catObj = dialogCategories.find(c => c.id === addProductCategory);
		const subObj = dialogSubCategories.find(s => s.id === addProductSubCategory);
		if (!deptObj || !catObj || !subObj) return;
		setIsAddProductDialogOpen(false);
		setLoadBackdrop(true);
		fetch(`${API_BASE}/addNewProduct`, {
			method: 'POST',
			headers: {
				"Content-Type": "application/json",
				"X-Session-Token": sessionToken()
			},
			body: JSON.stringify({
				subCategory: subObj.subCategoryName,
				venderLoggedUserName: getUserName(),
				vendorId: vendorId,
				department: deptObj.departmentName,
				category: catObj.categoryName
			})
		})
			.then(res => res.json())
			.then(data => {
				setLoadBackdrop(false);
				navigate(`/ap/${subObj.subCategoryName}`, {
					state: {
						subCategory: subObj.subCategoryName,
						productID: `${data.product.id}`,
						pricingID: `${data.pricing.id}`,
						inventoryID: `${data.inventory.id}`,
						additionalInfoID: `${data.additionInfo.id}`,
						fulfillmentID: `${data.fulfilment.id}`,
						imagesID: `${data.images.id}`,
						venderLoggerUserName: getUserName(),
						vendorId: vendorId,
						department: deptObj.departmentName,
						category: catObj.categoryName
					}
				});
			})
			.catch(error => {
				console.error("Error adding new product:", error);
				setLoadBackdrop(false);
			});
	};

	const handleGenerateProduct = () => {
		const AI_BASE = `${config.API_MWSSTOREMANAGERAI_BASE_URL}/api/v1`;
		let fetchURL = "";
		let bodyData = {};
		if (aiTab === "image" && imagePreview) {
			fetchURL = `${AI_BASE}/process-image`;
			bodyData = {image_base64: imagePreview};
		} else if (aiTab === "text" && aiDescription) {
			fetchURL = `${AI_BASE}/process-product-name`;
			bodyData = {productName: aiDescription};
		} else {
			return;
		}
		setIsAIDialogOpen(false);
		setLoadBackdrop(true);
		fetch(fetchURL, {
			method: 'POST',
			headers: {
				"Content-Type": "application/json",
				"X-Session-Token": sessionToken()
			},
			body: JSON.stringify(bodyData)
		})
			.then(res => {
				if (res.status === 200) {
					return res.json();
				} else {
					const aiErr = new Error("AI generation failed");
					aiErr.userMessage = aiErrorMessage(res.status, {imageUpload: aiTab === "image"});
					throw aiErr;
				}
			})
			.then(aiData => {
				const deptObj = departments.find(d => d.id === selectedDepartmentId);
				const catObj = categories.find(c => c.id === selectedCategoryId);
				const subObj = subCategories.find(s => s.id === selectedSubCategoryId);
				if (!deptObj || !catObj || !subObj) throw new Error("Category not selected for AI product");
				fetch(`${API_BASE}/addNewProduct`, {
					method: 'POST',
					headers: {
						"Content-Type": "application/json",
						"X-Session-Token": sessionToken()
					},
					body: JSON.stringify({
						subCategory: subObj.subCategoryName,
						venderLoggedUserName: getUserName(),
						vendorId: vendorId,
						department: deptObj.departmentName,
						category: catObj.categoryName
					})
				})
					.then(res => res.json())
					.then(newProdData => {
						setLoadBackdrop(false);
						navigate(`/ap/${subObj.subCategoryName}`, {
							state: {
								subCategory: subObj.subCategoryName,
								productID: `${newProdData.product.id}`,
								pricingID: `${newProdData.pricing.id}`,
								inventoryID: `${newProdData.inventory.id}`,
								additionalInfoID: `${newProdData.additionInfo.id}`,
								fulfillmentID: `${newProdData.fulfilment.id}`,
								imagesID: `${newProdData.images.id}`,
								venderLoggerUserName: getUserName(),
								vendorId: vendorId,
								department: deptObj.departmentName,
								category: catObj.categoryName,
								aiProduct: aiData,
								aiProductName: aiData.PN,
								aiProductDisplayName: aiData.PDN,
								aiProductDescription: aiData.LD,
								aiProductShortDescription: aiData.SD,
								aiProductAllergicContent: aiData.PAC
							}
						});
					})
					.catch(err => {
						console.error("Error adding new AI-generated product:", err);
						setLoadBackdrop(false);
						setAiErrorMsg("");
						setAiApiError(true);
					});
			})
			.catch(error => {
				console.error("AI generation error:", error);
				setLoadBackdrop(false);
				setAiErrorMsg(error.userMessage || "");
				setAiApiError(true);
			});
	};

	const handleFileSelect = (event) => {
		const file = event.target.files[0];
		if (file && file.size > 5 * 1024 * 1024) {
			event.target.value = "";
			setAiErrorMsg(AI_IMAGE_TOO_LARGE);
			setAiApiError(true);
			return;
		}
		if (file) {
			const reader = new FileReader();
			reader.onloadend = () => {
				setImagePreview(reader.result.toString());
				setImageFile(file);
				setAiDescription("");
			};
			reader.readAsDataURL(file);
		}
	};

	const handleEditProduct = (product) => {
		setLoadBackdrop(true);

		const productId = product?.productId;
		if (!productId) {
			console.error("handleEditProduct: missing productId", product);
			setLoadBackdrop(false);
			return;
		}

		// 1) Fetch the main product info (POST can keep Content-Type)
		fetch(`${API_BASE}/fetchAllProductInfo`, {
			method: 'POST',
			headers: {
				"Content-Type": "application/json",
				"X-Session-Token": sessionToken()
			},
			body: JSON.stringify({productId: String(productId), vendorId})
		})
			.then(res => {
				if (!res.ok) return res.text().then(t => {
					throw new Error(`fetchAllProductInfo ${res.status}: ${t}`);
				});
				return res.json();
			})
			.then(productData => {
				const token = sessionToken();
				const headers = token
					? {Accept: "application/json", "X-Session-Token": token}
					: {Accept: "application/json"};

				return fetch(
					`${API_BASE}/variant/by-product/${encodeURIComponent(productId)}?vendorId=${encodeURIComponent(vendorId)}`,
					{
						method: "GET",
						headers
					}
				)
					.then(res => {
						if (!res.ok) return res.text().then(t => {
							throw new Error(`variants GET ${res.status}: ${t}`);
						});
						return res.json();
					})
					.then(variantData => ({productData, variantData}));
			})
			.then(({productData, variantData}) => {
				setLoadBackdrop(false);
				const subObj = subCategories.find(s => s.subCategoryName === product.subCategory) || {};
				navigate(`/ep/${subObj.subCategoryName || ""}`, {
					state: {data: productData, data1: variantData}
				});
			})
			.catch(err => {
				console.error("Error fetching product details/variants:", err);
				setLoadBackdrop(false);
			});
	};

	// Delete = set productStatus "Unavailable"; Restore = "Available".
	// The fulfilment route copies every field it receives, so load the current
	// fulfilment first and send it back with only the status changed.
	const changeProductStatus = async (productId, newStatus) => {
		const headers = {
			"Content-Type": "application/json",
			Accept: "application/json",
			"X-Session-Token": sessionToken()
		};
		setLoadBackdrop(true);
		try {
			const infoRes = await fetch(`${API_BASE}/fetchAllProductInfo`, {
				method: 'POST',
				headers,
				body: JSON.stringify({productId: String(productId), vendorId})
			});
			if (!infoRes.ok) throw new Error(`fetchAllProductInfo ${infoRes.status}`);
			const info = await infoRes.json();
			const fulfilment = info?.fulfilment;
			if (!fulfilment?.id) throw new Error("Fulfilment details not found");

			const updRes = await fetch(`${API_BASE}/updateProductFulfilment`, {
				method: 'POST',
				headers,
				body: JSON.stringify({
					productId: String(productId),
					vendorId,
					smProductFulfilment: {
						id: fulfilment.id,
						deliveryOption: fulfilment.deliveryOption,
						fulfillment: fulfilment.fulfillment,
						productStatus: newStatus,
						creationDateTime: fulfilment.creationDateTime,
						createdBy: fulfilment.createdBy,
						updatedDateTime: Date.now(),
						updatedBy: fulfilment.updatedBy
					}
				})
			});
			if (!updRes.ok) throw new Error(`updateProductFulfilment ${updRes.status}`);
			// This API answers HTTP 200 on failure, so check the body.
			const result = await updRes.json();
			if (String(result?.status).toLowerCase() !== "success") {
				throw new Error(result?.errorMessage || "Update failed");
			}

			const apply = (list) => list.map(p =>
				p.productId === productId ? {...p, productStatus: newStatus} : p);
			const updated = apply(products);
			setProducts(updated);
			setAllProducts(prev => apply(prev));
			computeStats(updated);
			setStatusToast({
				open: true,
				severity: "success",
				message: newStatus === "Unavailable"
					? "Product removed from your store. You can restore it any time."
					: "Product restored and visible in your store."
			});
		} catch (err) {
			console.error("Error changing product status:", err);
			setStatusToast({
				open: true,
				severity: "error",
				message: newStatus === "Unavailable"
					? "Could not remove the product. Please try again."
					: "Could not restore the product. Please try again."
			});
		} finally {
			setLoadBackdrop(false);
		}
	};

	const confirmDelete = () => {
		if (deleteTarget == null) return;
		const target = deleteTarget;
		setDeleteTarget(null);
		changeProductStatus(target, "Unavailable");
	};

	const restoreProduct = (productId) => changeProductStatus(productId, "Available");

	const formatCurrency = (val) => {
		if (val >= 1000) {
			return `₹${(val / 1000).toFixed(1)}K`;
		}
		return `₹${val.toFixed(2)}`;
	};

	const departmentObj = departments.find(d => d.id === selectedDepartmentId);
	const categoryObj = categories.find(c => c.id === selectedCategoryId);
	const subCategoryObj = subCategories.find(s => s.id === selectedSubCategoryId);
	const categoryPath = departmentObj && categoryObj && subCategoryObj
		? `${departmentObj.departmentName} → ${categoryObj.categoryName} → ${subCategoryObj.subCategoryName}`
		: "";

	return (
		<Box sx={{minHeight: '100vh', bgcolor: '#f1f5f9'}}>
			<ThemeProvider theme={theme}>
				<Box sx={{borderBottom: '1px solid #E5E7EB', bgcolor: '#FFFFFF', position: 'sticky', top: 0, zIndex: 1000}}>
					<Container maxWidth="lg" sx={{py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
						<Box sx={{display: 'flex', alignItems: 'center', gap:  isMdUp ? 3:1}}>
							<Button
								onClick={() => navigate(-1)}
								startIcon={<ArrowBackIosNewIcon />}
								variant="outlined"
								size="small" color="secondary"
								sx={{
									borderRadius: 999,
									textTransform: "none",
									px: 1.25, py: 0.75, fontWeight: 600,
									borderColor: "divider",
									bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
									"&:hover": { bgcolor: (t) => t.palette.action.hover },
								}}
							>
								Back
							</Button>
							<Box>
								<Typography variant="h6" noWrap>{`Product Management`}</Typography>
								<Typography variant="body2" color="textSecondary" sx={{display: {xs: 'none', sm: 'block'}}}>
									Manage your product catalog efficiently
								</Typography>
							</Box>
						</Box>
						<Button
							variant="contained" color="primary"
							onClick={() => setIsAddProductDialogOpen(true)}
							startIcon={<AddIcon/>}
							sx={{
								textTransform: 'none',
								fontWeight: 600,
								px: {xs: 2, sm: 3}, ml:1,
								minWidth: {xs: 74, sm: 'auto'},
							}}
						>
							<Box component="span" sx={{display: {xs: 'block', sm: 'none'}}}>
								Add
							</Box>
							<Box component="span" sx={{display: {xs: 'none', sm: 'block'}}}>
								Add Product
							</Box>
						</Button>
					</Container>
				</Box>

				<Container maxWidth="lg" sx={{py: 3}}>
					<Box
						sx={{
							display: 'grid',
							gap: 2,
							gridTemplateColumns: {xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)'},
						}}
					>
						<Box>
							<Card sx={{...cardSurface, p: 0}}>
								<CardContent sx={{p: 2}}>
									<Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
										<Box>
											<Typography variant="subtitle2" color="textSecondary">Total Products</Typography>
											<Typography variant="h6">{stats.total}</Typography>
										</Box>
										<InventoryIcon sx={{fontSize: 32, color: '#2563EB'}}/>
									</Box>
								</CardContent>
							</Card>
						</Box>
						<Box>
							<Card sx={{...cardSurface, p: 0}}>
								<CardContent sx={{p: 2}}>
									<Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
										<Box>
											<Typography variant="subtitle2" color="textSecondary">Out of Stock</Typography>
											<Typography variant="h6">{stats.outOfStock}</Typography>
										</Box>
										<ShoppingCartIcon sx={{fontSize: 32, color: '#DC2626'}}/>
									</Box>
								</CardContent>
							</Card>
						</Box>
						<Box>
							<Card sx={{...cardSurface, p: 0}}>
								<CardContent sx={{p: 2}}>
									<Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
										<Box>
											<Typography variant="subtitle2" color="textSecondary">Low Stock</Typography>
											<Typography variant="h6">{stats.lowStock}</Typography>
										</Box>
										<WarningAmberIcon sx={{fontSize: 32, color: '#EA580C'}}/>
									</Box>
								</CardContent>
							</Card>
						</Box>
						<Box>
							<Card sx={{...cardSurface, p: 0}}>
								<CardContent sx={{p: 2}}>
									<Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
										<Box>
											<Typography variant="subtitle2" color="textSecondary">Total Value</Typography>
											<Typography variant="h6">{formatCurrency(stats.totalValue)}</Typography>
										</Box>
										<AttachMoneyIcon sx={{fontSize: 32, color: '#7C3AED'}}/>
									</Box>
								</CardContent>
							</Card>
						</Box>
					</Box>
				</Container>

				{!isLgUp && (
					<Box sx={{display: {lg: 'none'}, px: 2, mb: 3}}>
						<Card sx={cardSurface}>
							<CardHeader
								title="Product Categories"
								subheader="Select categories to view and manage products"
								sx={{pb: 1}}
							/>
							<CardContent sx={{pt: 0}}>
								<Typography variant="subtitle2" gutterBottom>Department</Typography>
								<Select
									fullWidth
									size="small"
									value={selectedDepartmentId}
									onChange={handleDepartmentChange}
									displayEmpty
								>
									<MenuItem value="" disabled>Select Department</MenuItem>
									{departments.map(dep => (
										<MenuItem key={dep.id} value={dep.id}>{dep.departmentName}</MenuItem>
									))}
									<MenuItem value={ADD_NEW_DEPT}>
										<AddIcon fontSize="small" style={{marginRight: 8}}/>
										Add new department
									</MenuItem>
								</Select>

								<Typography variant="subtitle2" sx={{mt: 2}} gutterBottom>Category</Typography>
								<Select
									fullWidth
									size="small"
									value={selectedCategoryId}
									onChange={handleCategoryChange}
									displayEmpty
									disabled={!selectedDepartmentId}
								>
									<MenuItem value="" disabled>Select Category</MenuItem>
									{categories.map(cat => (
										<MenuItem key={cat.id} value={cat.id}>{cat.categoryName}</MenuItem>
									))}
									<MenuItem value={ADD_NEW_CAT} disabled={!selectedDepartmentId}>
										<AddIcon fontSize="small" style={{marginRight: 8}}/>
										Add new category
									</MenuItem>
								</Select>

								<Typography variant="subtitle2" sx={{mt: 2}} gutterBottom>Sub-Category</Typography>
								<Select
									fullWidth
									size="small"
									value={selectedSubCategoryId}
									onChange={handleSubCategoryChange}
									displayEmpty
									disabled={!selectedCategoryId}
								>
									<MenuItem value="" disabled>Select Sub-Category</MenuItem>
									{subCategories.map(sub => (
										<MenuItem key={sub.id} value={sub.id}>{sub.subCategoryName}</MenuItem>
									))}
									<MenuItem value={ADD_NEW_SUB} disabled={!selectedCategoryId}>
										<AddIcon fontSize="small" style={{marginRight: 8}}/>
										Add new sub-category
									</MenuItem>
								</Select>
								<Box sx={{mt: 3, display: 'flex', flexDirection: 'column', gap: 1.25}}>
									<Typography variant="subtitle2" gutterBottom>Add New Product</Typography>

									<Button
										variant="outlined"
										fullWidth
										startIcon={<AutoFixHighIcon/>}
										color="secondary"
										sx={{
											textAlign: 'left',
											borderColor: '#a0a0a0',
											color: '#4B5563',
											fontWeight: 600,
											'&:hover': {borderColor: '#D1D5DB', bgcolor: '#ff9500', color: '#000'},
										}}
										onClick={() => setIsAIDialogOpen(true)}
										disabled={!(selectedDepartmentId && selectedCategoryId && selectedSubCategoryId)}
									>
										Add Product with AI
									</Button>

									<Button
										variant="outlined"
										fullWidth
										startIcon={<AddIcon/>}
										color="secondary"
										sx={{
											textAlign: 'left',
											borderColor: '#a0a0a0',
											color: '#4B5563',
											fontWeight: 600,
											'&:hover': {borderColor: '#D1D5DB', bgcolor: '#007eff', color: '#fff'},
										}}
										onClick={handleAddProductFromSelected}
										disabled={!(selectedDepartmentId && selectedCategoryId && selectedSubCategoryId)}
									>
										Add Product Manually
									</Button>
								</Box>
							</CardContent>
						</Card>
					</Box>
				)}

				<Container
					maxWidth="lg"
					sx={{
						display: 'flex',
						flexDirection: {xs: 'column', lg: 'row'},
						gap: {xs: 2, lg: 4},
						pb: 6,
						overflowX: 'hidden',
					}}
				>
					<Box sx={{flex: '0 0 280px', display: {xs: 'none', lg: 'block'}}}>
						<Card sx={{...cardSurface}}>
							<CardHeader
								title="Product Categories"
								subheader="Select categories to view and manage products"
							/>
							<CardContent>
								<Typography variant="subtitle2" gutterBottom>Department</Typography>
								<Select
									fullWidth
									size="small"
									value={selectedDepartmentId}
									onChange={handleDepartmentChange}
									displayEmpty
								>
									<MenuItem value="" disabled>Select Department</MenuItem>
									{departments.map(dep => (
										<MenuItem key={dep.id} value={dep.id}>{dep.departmentName}</MenuItem>
									))}
									<MenuItem value={ADD_NEW_DEPT}>
										<AddIcon fontSize="small" style={{marginRight: 8}}/>
										Add new department
									</MenuItem>
								</Select>

								<Typography variant="subtitle2" sx={{mt: 2}} gutterBottom>Category</Typography>
								<Select
									fullWidth
									size="small"
									value={selectedCategoryId}
									onChange={handleCategoryChange}
									displayEmpty
									disabled={!selectedDepartmentId}
								>
									<MenuItem value="" disabled>Select Category</MenuItem>
									{categories.map(cat => (
										<MenuItem key={cat.id} value={cat.id}>{cat.categoryName}</MenuItem>
									))}
									<MenuItem value={ADD_NEW_CAT} disabled={!selectedDepartmentId}>
										<AddIcon fontSize="small" style={{marginRight: 8}}/>
										Add new category
									</MenuItem>
								</Select>

								<Typography variant="subtitle2" sx={{mt: 2}} gutterBottom>Sub-Category</Typography>
								<Select
									fullWidth
									size="small"
									value={selectedSubCategoryId}
									onChange={handleSubCategoryChange}
									displayEmpty
									disabled={!selectedCategoryId}
								>
									<MenuItem value="" disabled>Select Sub-Category</MenuItem>
									{subCategories.map(sub => (
										<MenuItem key={sub.id} value={sub.id}>{sub.subCategoryName}</MenuItem>
									))}
									<MenuItem value={ADD_NEW_SUB} disabled={!selectedCategoryId}>
										<AddIcon fontSize="small" style={{marginRight: 8}}/>
										Add new sub-category
									</MenuItem>
								</Select>

								<Box sx={{mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5}}>
									<Typography variant="subtitle2" gutterBottom>Add New Product</Typography>
									<Button
										variant="outlined"
										fullWidth
										startIcon={<AutoFixHighIcon/>}
										color="secondary"
										sx={{
											textAlign: 'left',
											borderColor: '#a0a0a0',
											color: '#4B5563',
											fontWeight: 600,
											'&:hover': {borderColor: '#D1D5DB', bgcolor: '#ff9500', color: '#000'},
										}}
										onClick={() => setIsAIDialogOpen(true)}
										disabled={!(selectedDepartmentId && selectedCategoryId && selectedSubCategoryId)}
									>
										Add Product with AI
									</Button>
									<Button
										variant="outlined"
										fullWidth
										startIcon={<AddIcon/>}
										color="secondary"
										sx={{
											textAlign: 'left',
											borderColor: '#a0a0a0',
											color: '#4B5563',
											fontWeight: 600,
											'&:hover': {borderColor: '#D1D5DB', bgcolor: '#007eff', color: '#fff'},
										}}
										onClick={handleAddProductFromSelected}
										disabled={!(selectedDepartmentId && selectedCategoryId && selectedSubCategoryId)}
									>
										Add Product Manually
									</Button>
								</Box>
							</CardContent>
						</Card>
					</Box>

					<Box sx={{flex: 1}}>
						<Card sx={{...cardSurface, p: 0}}>
							<CardHeader
								title="Products"
								subheader={categoryPath || "Select categories to view products"}
								action={
									<IconButton size="small" sx={{display: {lg: 'none'}}} onClick={() => setCategoriesDrawerOpen(true)}>
										<FilterListIcon/>
									</IconButton>
								}
							/>
							<CardContent>
								<Box sx={{px: {xs: 0.5, sm: 0}}}>
									{categoryPath && (
										<Box sx={{mb: 3}}>
											<Box
												sx={{
													display: 'grid',
													gridTemplateColumns: {xs: '1fr', sm: '1fr 160px auto'},
													gap: 2,
													alignItems: 'center',
												}}
											>
												{searchType === 'priceRange' ? (
													<Box
														sx={{
															display: 'grid',
															gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
															gap: 1,
														}}
													>
														<TextField
															label="Min Price"
															size="small"
															value={searchValueMin}
															onChange={(e) => setSearchValueMin(e.target.value)}
															onKeyDown={handleSearchKeyDown}
														/>
														<TextField
															label="Max Price"
															size="small"
															value={searchValueMax}
															onChange={(e) => setSearchValueMax(e.target.value)}
															onKeyDown={handleSearchKeyDown}
														/>
													</Box>
												) : (
													<TextField
														placeholder="Search products..."
														size="small"
														value={searchQuery}
														onChange={(e) => setSearchQuery(e.target.value)}
														onKeyDown={handleSearchKeyDown}
														sx={{
															'& .MuiOutlinedInput-root': {borderRadius: 1.5, backgroundColor: '#fff'},
															'& .MuiOutlinedInput-input': {py: 1.1},
														}}
														InputProps={{
															startAdornment: (
																<InputAdornment position="start" sx={{pl: 0.5}}>
																	<SearchIcon sx={{fontSize: 18, color: '#9CA3AF'}}/>
																</InputAdornment>
															),
															endAdornment: searchQuery ? (
																<InputAdornment position="end">
																	<IconButton size="small" onClick={() => setSearchQuery('')}>
																		<ClearIcon fontSize="small"/>
																	</IconButton>
																</InputAdornment>
															) : null,
														}}
													/>
												)}

												<Select
													value={searchType}
													size="small"
													onChange={handleSearchTypeChange}
													sx={{
														width: {xs: '100%', sm: 160},
														justifySelf: {xs: 'stretch', sm: 'start'},
														'& .MuiOutlinedInput-root': {borderRadius: 1.5, backgroundColor: '#fff'},
														'& .MuiOutlinedInput-input': {py: 1.1},
													}}
												>
													<MenuItem value="name">Name</MenuItem>
													<MenuItem value="SKU">SKU</MenuItem>
													<MenuItem value="priceRange">Price Range</MenuItem>
													<MenuItem value="productId">Product ID</MenuItem>
													<MenuItem value="barcode">Barcode</MenuItem>
												</Select>

												<Box
													sx={{
														display: 'flex',
														gap: 1.25,
														justifyContent: {xs: 'flex-start', sm: 'flex-end'},
														alignItems: 'center',
														flexWrap: 'nowrap',
													}}
												>
													<LoadingButton
														variant="contained"
														onClick={handleSearch}
														loading={searchLoading}
														startIcon={!searchLoading && <SearchIcon/>}
														loadingPosition="start"
														disableElevation
														sx={{
															minHeight: 40,
															borderRadius: 1.5,
															textTransform: 'none',
															fontWeight: 600,
															px: 2.5,
														}}
													>
														{searchLoading ? 'Searching' : 'Search'}
													</LoadingButton>

													<Button
														variant="text"
														startIcon={<ClearIcon/>}
														onClick={() => {
															setSearchQuery('');
															setSearchValueMin('');
															setSearchValueMax('');
															const deptObj = departments.find(d => d.id === selectedDepartmentId);
															const catObj = categories.find(c => c.id === selectedCategoryId);
															const subObj = subCategories.find(s => s.id === selectedSubCategoryId);
															if (deptObj && catObj && subObj) {
																loadProducts(deptObj.departmentName, catObj.categoryName, subObj.subCategoryName);
															}
														}}
														sx={{
															minHeight: 40,
															borderRadius: 1.5,
															textTransform: 'none',
															fontWeight: 600,
															color: 'text.secondary',
															'&:hover': {bgcolor: 'action.hover'},
														}}
													>
														Clear
													</Button>
												</Box>
											</Box>
										</Box>
									)}

									{!categoryPath ? (
										<Typography color="textSecondary" align="center" sx={{my: 4}}>
											Please select a Department, Category, and Sub-Category to view products.
										</Typography>
									) : loadingProducts ? (
										<Box sx={{textAlign: 'center', py: 4}}>
											<CircularProgress/>
										</Box>
									) : filteredProducts.length === 0 ? (
										<Box sx={{textAlign: 'center', py: 6}}>
											<Inventory2OutlinedIcon sx={{fontSize: 64, color: 'grey.300'}}/>
											<Typography variant="h6" sx={{mt: 2}}>No products found</Typography>
											<Typography color="textSecondary" sx={{mb: 3}}>
												{searchQuery
													? `No products match your search in ${categoryPath}`
													: `No products found in ${categoryPath}`}
											</Typography>
											{!searchQuery && (
												<Button variant="contained" color="primary" startIcon={<AddIcon/>} onClick={handleAddProductFromSelected}>
													Add Product
												</Button>
											)}
										</Box>
									) : (
										isMdUp ? (
											<Box sx={{overflowX: 'auto'}}>
												<TableContainer>
													<Table sx={{minWidth: 750}}>
														<TableHead>
															<TableRow sx={{backgroundColor: '#F9FAFB'}}>
																<TableCell sx={{width: 72}}>Image</TableCell>
																<TableCell>Product Name</TableCell>
																<TableCell>SKU</TableCell>
																<TableCell>Price</TableCell>
																<TableCell>Status</TableCell>
																<TableCell>Stock</TableCell>
																<TableCell align="right">Actions</TableCell>
															</TableRow>
														</TableHead>

														<TableBody>
															{filteredProducts.map((product) => {
																const stock = Number(product.inStock ?? 0)
																const lowStockThresh = Number(product.lowStock ?? 0)
																const status = product.productStatus

																let statusLabel = status || 'Active'
																let statusColor = 'default'
																if (status !== 'Unavailable') {
																	if (stock === 0) {
																		statusLabel = 'Out of Stock';
																		statusColor = 'error'
																	} else if (lowStockThresh > 0 && stock < lowStockThresh) {
																		statusLabel = 'Low Stock';
																		statusColor = 'error';
																	}
																} else {
																	statusLabel = 'Unavailable'
																}

																return (
																	<TableRow
																		key={product.productId}
																		hover
																		sx={{
																			'&:not(:last-of-type) td': {borderBottom: '1px solid #E5E7EB'}, // horizontal only
																		}}
																	>
																		<TableCell>
																			<Box
																				component="img"
																				src={product.presignedURL}
																				sx={{width: 40, height: 40, borderRadius: 1, objectFit: 'cover'}}
																			/>
																		</TableCell>

																		<TableCell>
																			<Typography variant="body2" fontWeight={600}>
																				{product.name}
																			</Typography>
																		</TableCell>

																		<TableCell>
																			<Typography variant="caption" color="text.secondary">
																				{product.sku}
																			</Typography>
																		</TableCell>

																		<TableCell>₹{Number(product.pricePerUnit ?? 0).toFixed(2)}</TableCell>

																		<TableCell>
																			<Chip
																				label={statusLabel}
																				size="small"
																				color={statusColor === 'error' ? 'error' : 'default'}
																				sx={statusLabel === 'Active' ? {bgcolor: '#000', color: '#fff'} : {}}
																			/>
																		</TableCell>

																		<TableCell>
																			<Typography
																				variant="body2"
																				sx={{
																					fontWeight: stock > 0 && lowStockThresh > 0 && stock < lowStockThresh ? 600 : 400,
																					color: stock > 0 && lowStockThresh > 0 && stock < lowStockThresh ? 'error.main' : 'text.primary',
																				}}
																			>
																				{stock}
																			</Typography>
																		</TableCell>

																		<TableCell align="right">
																			<Box sx={{display: 'flex', flexDirection: 'row', gap: 1}}>
																				<IconButton size="small" onClick={() => handleEditProduct(product)}>
																					<EditIcon fontSize="small"/>
																				</IconButton>
																				<IconButton
	size="small"
	aria-label={product.productStatus === 'Unavailable' ? 'Show in store' : 'Delete product'}
	title={product.productStatus === 'Unavailable' ? 'Show in store' : 'Delete'}
	onClick={() => product.productStatus === 'Unavailable'
		? restoreProduct(product.productId)
		: setDeleteTarget(product.productId)}
>
																					{product.productStatus === 'Unavailable'
	? <VisibilityIcon fontSize="small"/>
	: <DeleteIcon fontSize="small"/>}
																				</IconButton>
																			</Box>
																		</TableCell>
																	</TableRow>
																)
															})}
														</TableBody>
													</Table>
												</TableContainer>
											</Box>
										) : (
											<Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
												{filteredProducts.map(product => {
													const stock = Number(product.inStock ?? 0)
													const lowStockThresh = Number(product.lowStock ?? 0)
													const status = product.productStatus

													let statusLabel = status || 'Active'
													let statusColor = 'default'
													if (status !== 'Unavailable') {
														if (stock === 0) {
															statusLabel = 'Out of Stock';
															statusColor = 'error'
														} else if (stock <= lowStockThresh) {
															statusLabel = 'Low Stock';
															statusColor = 'error'
														}
													} else {
														statusLabel = 'Unavailable'
													}

													return (
														<Card
															key={product.productId}
															sx={{...cardSurface, p: 2, borderRadius: 3}}
														>
															<Box sx={{display: 'flex', gap: 2}}>
																<Box
																	component="img"
																	src={product.presignedURL}
																	sx={{width: 48, height: 48, borderRadius: 1, objectFit: 'cover'}}
																/>
																<Box sx={{flex: 1, minWidth: 0}}>
																	<Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>
																		<Typography variant="subtitle2" sx={{fontWeight: 600, mr: 1, flex: 1}} noWrap>
																			{product.name}
																		</Typography>
																		<Typography variant="subtitle2">
																			₹{Number(product.pricePerUnit ?? 0).toFixed(2)}
																		</Typography>
																	</Box>

																	<Typography variant="caption" color="text.secondary">
																		{product.sku}
																	</Typography>

																	<Box
																		sx={{
																			mt: 1.5,
																			display: 'flex',
																			alignItems: 'center',
																			justifyContent: 'space-between',
																		}}
																	>
																		<Chip
																			label={statusLabel}
																			size="small"
																			color={statusColor === 'error' ? 'error' : 'default'}
																			sx={statusLabel === 'Active' ? {bgcolor: '#000', color: '#fff'} : {}}
																		/>

																		<Typography
																			variant="caption"
																			sx={{
																				fontWeight: stock > 0 && stock <= lowStockThresh ? 600 : 400,
																				color: stock > 0 && lowStockThresh > 0 && stock < lowStockThresh ? 'error.main' : 'text.secondary',
																			}}
																		>
																			Stock:&nbsp;{stock}
																		</Typography>

																		<Box>
																			<IconButton size="small" onClick={() => handleEditProduct(product)}>
																				<EditIcon fontSize="small"/>
																			</IconButton>
																			<IconButton
	size="small"
	aria-label={product.productStatus === 'Unavailable' ? 'Show in store' : 'Delete product'}
	title={product.productStatus === 'Unavailable' ? 'Show in store' : 'Delete'}
	onClick={() => product.productStatus === 'Unavailable'
		? restoreProduct(product.productId)
		: setDeleteTarget(product.productId)}
>
																				{product.productStatus === 'Unavailable'
	? <VisibilityIcon fontSize="small"/>
	: <DeleteIcon fontSize="small"/>}
																			</IconButton>
																		</Box>
																	</Box>
																</Box>
															</Box>
														</Card>
													)
												})}
											</Box>
										)
									)}
								</Box>
							</CardContent>
						</Card>
					</Box>
				</Container>

				<Dialog open={isAddProductDialogOpen} onClose={() => setIsAddProductDialogOpen(false)}>
					<DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
						<AddIcon color="primary"/> Add New Product
					</DialogTitle>
					<DialogContent>
						<DialogContentText sx={{mb: 2}}>
							Select the department, category, and sub-category for your new product.
						</DialogContentText>
						<Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300}}>
							<Select value={addProductDepartment} onChange={handleAddDeptChange} displayEmpty>
								<MenuItem value="">Select Department</MenuItem>
								{departments.map(dep => (
									<MenuItem key={dep.id} value={dep.id}>{dep.departmentName}</MenuItem>
								))}
								<MenuItem value={ADD_NEW_DEPT}>
									<AddIcon fontSize="small" style={{marginRight: 8}}/>
									Add new department
								</MenuItem>
							</Select>
							<Select value={addProductCategory} onChange={handleAddCategoryChange} displayEmpty disabled={!addProductDepartment}>
								<MenuItem value="">Select Category</MenuItem>
								{dialogCategories.map(cat => (
									<MenuItem key={cat.id} value={cat.id}>{cat.categoryName}</MenuItem>
								))}
								<MenuItem value={ADD_NEW_CAT} disabled={!selectedDepartmentId}>
									<AddIcon fontSize="small" style={{marginRight: 8}}/>
									Add new category
								</MenuItem>
							</Select>
							<Select value={addProductSubCategory} onChange={handleAddSubCategoryChange} displayEmpty disabled={!addProductCategory}>
								<MenuItem value="">Select Sub-Category</MenuItem>
								{dialogSubCategories.map(sub => (
									<MenuItem key={sub.id} value={sub.id}>{sub.subCategoryName}</MenuItem>
								))}
								<MenuItem value={ADD_NEW_SUB} disabled={!selectedCategoryId}>
									<AddIcon fontSize="small" style={{marginRight: 8}}/>
									Add new sub-category
								</MenuItem>
							</Select>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button variant="outlined" color="secondary" onClick={() => setIsAddProductDialogOpen(false)}>Cancel</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={handleAddProductContinue}
							disabled={!(addProductDepartment && addProductCategory && addProductSubCategory)}
						>
							Continue to Product Details
						</Button>
					</DialogActions>
				</Dialog>

				<Dialog open={isAIDialogOpen} onClose={() => setIsAIDialogOpen(false)} fullWidth maxWidth="sm">
					<DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
						<AutoFixHighIcon color="secondary"/> Create Product with AI
					</DialogTitle>
					<DialogContent>
						<DialogContentText sx={{mb: 2}}>
							Upload an image or describe your product to generate details automatically.
						</DialogContentText>
						<Tabs
							value={aiTab}
							onChange={(e, val) => setAiTab(val)}
							centered
							variant="fullWidth"
							TabIndicatorProps={{style: {display: "none"}}}
							sx={{
								minHeight: "auto",
								backgroundColor: '#e7e7e7',
								my: 1, p: 0.5, borderRadius: '6px',
								"& .MuiTab-root": {
									textTransform: "none",
									fontWeight: 500,
									borderRadius: "6px",
									color: "#666",
									minHeight: "auto",
									py: 1,
								},
								"& .Mui-selected": {
									bgcolor: "#ff9100",
									color: "#fff!important",
									boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
								}
							}}
						>
							<Tab label="Upload Image" value="image"/>
							<Tab label="Describe Product" value="text"/>
						</Tabs>
						{aiTab === 'image' && (
							<Box sx={{textAlign: 'center', border: '2px dashed #ccc', borderRadius: 2, p: '12px', mb: 2}}>
								{imagePreview ? (
									<Box sx={{display:'flex', flexDirection:'column', gap:1}}>
										<img src={imagePreview} alt="Product" style={{maxWidth: '100%', maxHeight: 200}}/>
										<Button variant="text" color="secondary" onClick={() => {
											setImagePreview("");
											setImageFile(null);
										}} sx={{mt: 1}}>
											Change Image
										</Button>
									</Box>
								) : (
									<Box onClick={() => fileInputRef.current && fileInputRef.current.click()}>
										<CloudUploadIcon sx={{fontSize: 48, color: 'action.active', mb: 1}}/>
										<Typography variant="body2" color="textSecondary">
											Drag and drop an image here, or click to browse
										</Typography>
										<Button color='secondary' variant="outlined" sx={{mt: 1}}>Choose File</Button>
										<input
											type="file"
											accept="image/*"
											ref={fileInputRef}
											style={{display: 'none'}}
											onChange={handleFileSelect}
										/>
									</Box>
								)}
							</Box>
						)}
						{aiTab === 'text' && (
							<TextField
								fullWidth
								label="Product Description"
								placeholder='e.g., Wireless Bluetooth headphones with noise cancellation'
								multiline minRows={5}
								value={aiDescription}
								onChange={(e) => {
									setAiDescription(e.target.value);
									setImagePreview("");
								}}
								sx={{mb: 2, borderRadius: 2}}
							/>
						)}
						<Box sx={{backgroundColor: '#f5f5f5', p: 2, borderRadius: 1}}>
							<Typography variant="body2" color="textSecondary">Product will be added to:</Typography>
							<Typography variant="body2" fontWeight="bold">
								{categoryPath || " (please select a category first)"}
							</Typography>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button color='secondary' variant="outlined" onClick={() => setIsAIDialogOpen(false)}>Cancel</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={handleGenerateProduct}
							startIcon={<AutoFixHighIcon/>}
							disabled={!((aiTab === 'image' && imagePreview) || (aiTab === 'text' && aiDescription))}
						>
							Generate Product
						</Button>
					</DialogActions>
				</Dialog>

				<Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
					<DialogTitle>Remove Product</DialogTitle>
					<DialogContent>
						<DialogContentText>
							This product will be hidden from your store and marked Unavailable. You can restore it any time.
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDeleteTarget(null)} color="secondary">Cancel</Button>
						<Button onClick={confirmDelete} color="error">Delete</Button>
					</DialogActions>
				</Dialog>

				{loadBackdrop && (
					<Box
						sx={{
							position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
							bgcolor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 13000
						}}
					>
						<CircularProgress size={60} sx={{color: '#fff'}}/>
					</Box>
				)}

				<Snackbar
					open={statusToast.open}
					onClose={(e, reason) => {
						if (reason === "clickaway") return;
						setStatusToast(t => ({...t, open: false}));
					}}
					autoHideDuration={5000}
					anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
				>
					<Alert
						severity={statusToast.severity}
						onClose={() => setStatusToast(t => ({...t, open: false}))}
						sx={{width: '100%'}}
					>
						{statusToast.message}
					</Alert>
				</Snackbar>
				<Snackbar
					open={aiApiError}
					onClose={() => setAiApiError(false)}
					autoHideDuration={6000}
					anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
				>
					<Alert severity="error" onClose={() => setAiApiError(false)} sx={{width: '100%'}}>
						{aiErrorMsg || "Cannot generate details for the product. Please try again."}
					</Alert>
				</Snackbar>
				<Drawer
					anchor="right"
					open={categoriesDrawerOpen}
					onClose={() => setCategoriesDrawerOpen(false)}
					PaperProps={{sx: {width: 320}}}
				>
					<Box sx={{p: 2}}>
						<Typography variant="h6" sx={{mb: 2}}>Product Categories</Typography>

						<Typography variant="subtitle2" gutterBottom>Department</Typography>
						<Select
							fullWidth size="small"
							value={selectedDepartmentId}
							onChange={handleDepartmentChange}
							displayEmpty
						>
							<MenuItem value="" disabled>Select Department</MenuItem>
							{departments.map(dep => (
								<MenuItem key={dep.id} value={dep.id}>{dep.departmentName}</MenuItem>
							))}
							<MenuItem value={ADD_NEW_DEPT}>
								<AddIcon fontSize="small" style={{marginRight: 8}}/>
								Add new department
							</MenuItem>
						</Select>

						<Typography variant="subtitle2" sx={{mt: 2}} gutterBottom>Category</Typography>
						<Select
							fullWidth size="small"
							value={selectedCategoryId}
							onChange={handleCategoryChange}
							displayEmpty
							disabled={!selectedDepartmentId}
						>
							<MenuItem value="" disabled>Select Category</MenuItem>
							{categories.map(cat => (
								<MenuItem key={cat.id} value={cat.id}>{cat.categoryName}</MenuItem>
							))}
							<MenuItem value={ADD_NEW_CAT} disabled={!selectedDepartmentId}>
								<AddIcon fontSize="small" style={{marginRight: 8}}/>
								Add new category
							</MenuItem>
						</Select>

						<Typography variant="subtitle2" sx={{mt: 2}} gutterBottom>Sub-Category</Typography>
						<Select
							fullWidth size="small"
							value={selectedSubCategoryId}
							onChange={handleSubCategoryChange}
							displayEmpty
							disabled={!selectedCategoryId}
						>
							<MenuItem value="" disabled>Select Sub-Category</MenuItem>
							{subCategories.map(sub => (
								<MenuItem key={sub.id} value={sub.id}>{sub.subCategoryName}</MenuItem>
							))}
							<MenuItem value={ADD_NEW_SUB} disabled={!selectedCategoryId}>
								<AddIcon fontSize="small" style={{marginRight: 8}}/>
								Add new sub-category
							</MenuItem>
						</Select>

						<Box sx={{mt: 3}}>
							<Button fullWidth variant="contained" color="primary" onClick={() => setCategoriesDrawerOpen(false)}>
								Done
							</Button>
						</Box>
					</Box>
				</Drawer>
			</ThemeProvider>
		</Box>
	);
}
